/* global module*/

import React from 'react';
import { connect } from 'react-redux';
import { upperFirst as _upperFirst, startCase } from 'lodash';
import cx from 'classnames';
import Slider from '../../common/Slider/index.tsx';
import Help from '../../common/Help';
import { convertCaloriesToServings } from '../../../lib/apiKeys/food';
import { withT, wrap } from '../../../lib/wrappers';
import a, { bindActionCreators, pick } from '../../../redux/actions';
import s from '../../../redux/selectors';
import numericInput from '../../../lib/numericInput';
import { userApiValue, defaultApiValue } from '../../../lib/legacyHelpers';
import ResizableComponent from '../../../lib/base_classes/ResizableComponent';
import NavButtons from '../../common/NavButtons.tsx';
import { CustomizableButton } from '../../common/Customizable';

const RELEVANT_API_KEYS = ['meatfisheggs', 'meat_beefpork', 'meat_fish', 'meat_other', 'meat_poultry', 'cereals', 'dairy', 'fruitvegetables', 'otherfood'];
const MEAT_TYPES = ['meat_beefpork', 'meat_fish', 'meat_other', 'meat_poultry'];
const AVERAGE_HOUSEHOLD_SIZE = 2.5;

class FoodComponent extends ResizableComponent {

  constructor(props, context) {
    super(props, context);
    const food = this;
    food.sliders = [];
    food.initResizeListener();
    food.state = Object.assign({
      simple: true,
      width: window.innerWidth,
    });
  }

  get t() {
    return this.props.t;
  }

  get api_key_base() {
    return 'input_footprint_shopping_food';
  }

  apiKey(key_end) {
    return `${this.api_key_base}_${key_end}`;
  }

  get relevant_api_keys() {
    return RELEVANT_API_KEYS;
  }

  get helpText() {
    const servingHelpHref = this.props.widgetSource === 'ontario' ?
      this.t('www.canada.ca/en/health-canada/services/food-nutrition/canada-food-guide/food-guide-basics/what-food-guide-serving.html') :
      'www.heart.org/HEARTORG/HealthyLiving/HealthyEating/HealthyDietGoals/Suggested-Servings-from-Each-Food-Group_UCM_318186_Article.jsp#.WlvTPZM-dE4';
    const link = `
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://${servingHelpHref}"
      >${this.t('here')}</a>
    `;
    return `
      ${this.t('Enter the daily diet of the average person in your household. For example, if three people eat a total of six servings per day, enter 2 servings per person (6 ÷ 3 = 2).')}<br /><br />
      ${this.t('Find more help calculating your servings')} ${link}.
    `;
  }

  getFoodLabel(type) {
    const labels = {
      meatfisheggs: 'Meat, fish, eggs',
      meat_beefpork: 'Beef, pork, lamb, veal',
      meat_fish: 'Fish & seafood',
      meat_other: 'Other meat and meat alternatives (processed meat, nuts, etc.)',
      meat_poultry: 'Poultry & eggs',
      cereals: 'Grains & baked goods',
      dairy: 'Dairy',
      fruitvegetables: 'Fruits and vegetables',
      otherfood: 'Snacks, drinks, etc...',
    };
    if (!labels[type]) throw new Error(`No label for food type '${type}'.`);
    return this.t(labels[type]);
  }

  /*
   * Simple/Advanced
   */

  get simple() {
    return this.state.simple;
  }

  get advanced() {
    return !this.simple;
  }

  shouldShow(food_type) {
    const food = this;
    if (food.simple) {
      return MEAT_TYPES.indexOf(food_type) < 0;
    } else if (food_type === 'meatfisheggs') {
      return false;
    }
    return true;
  }

  setSimple() {
    const food = this;
    if (food.simple) return;
    food.setState({
      simple: true,
    });
    this.props.computeUserFootprint({
      input_footprint_shopping_food_meattype: 0,
    });
  }

  setAdvanced() {
    const food = this;
    if (food.advanced) return;
    food.setState({
      simple: false,
    });
    this.props.computeUserFootprint({
      input_footprint_shopping_food_meattype: 1,
    });
  }

  /*
   * Food Sliders
   */

  get household_size() {
    const { user_footprint, average_footprint } = this.props;
    const size = parseInt(userApiValue('input_size', user_footprint, average_footprint), 10);
    return size === 0 ? AVERAGE_HOUSEHOLD_SIZE : size;
  }

  displayUserServings(food_type) {
    const api_key = this.apiKey(food_type);
    const { user_footprint, average_footprint } = this.props;
    const daily_household_calories = userApiValue(api_key, user_footprint, average_footprint);
    const daily_user_calories = daily_household_calories / this.household_size;
    const servings = convertCaloriesToServings(api_key, daily_user_calories);
    return numericInput.format(servings, { decimalPlaces: 1 });
  }

  resize() {
    this.setState({ width: window.innerWidth });
  }

  updateMultiplier(foodType, multiplier) {
    const apiKey = this.apiKey(foodType);
    if (foodType === 'meatfisheggs') {
      this.distributeAverageMeatCalories(multiplier);
    } else {
      const update = {
        [apiKey]: this.applyAverageCalorieMultiplier(foodType, multiplier),
      };
      this.props.computeUserFootprint(update);
    }
  }

  distributeAverageMeatCalories(multiplier) {
    const food = this;
    const calorie_distribution = {};
    const update_params = MEAT_TYPES.reduce((hash, meat_type) => {
      const api_key = food.apiKey(meat_type);
      const calories = food.applyAverageCalorieMultiplier(meat_type, multiplier);
      calorie_distribution[api_key] = calories;
      return calorie_distribution;
    }, {});
    const total_api_key = food.apiKey('meatfisheggs');
    const total_meat = food.applyAverageCalorieMultiplier('meatfisheggs', multiplier);
    update_params[total_api_key] = total_meat;

    this.props.computeUserFootprint(update_params);
  }

  applyAverageCalorieMultiplier(food_type, multiplier) {
    const food = this;
    const api_key = food.apiKey(food_type);
    const { average_footprint } = this.props;
    const calorie_average = defaultApiValue(api_key, average_footprint);
    return multiplier * calorie_average;
  }

  render() {
    return (
      <div className="cc-component" id="food">
        <header className="cc-component__header">
          <h3 className="cc-component__byline">
            {this.t('How much does the average person in your household eat?')}
            <Help
              popoverProps={{ id: 'food_byline_help' }}
              helpText={this.helpText}
            />
          </h3>
          <div>
            <div className="btn-group" role="group" aria-label={this.t('Simple or advanced food questions')}>
              <CustomizableButton
                ui={this.props.ui}
                type="button"
                className={cx('btn', { 'btn-default': !this.state.simple, 'btn-success': this.state.simple })}
                onClick={() => this.setSimple()}
              >{startCase(this.t('Simple'))}</CustomizableButton>
              <CustomizableButton
                ui={this.props.ui}
                type="button"
                className={cx('btn', { 'btn-default': this.state.simple, 'btn-success': !this.state.simple })}
                onClick={() => this.setAdvanced()}
              >{this.t('Advanced')}</CustomizableButton>
            </div>
          </div>
        </header>

        <div className="cc-component__form">
          {this.relevant_api_keys.map(foodType => (
            <div
              key={foodType}
            >
              <div
                className={cx('cc-component__question', { hidden: !this.shouldShow(foodType) })}
              >
                <h4>{this.getFoodLabel(foodType)}</h4>
                <div className="label-container">
                  <span className="label label-slider">{this.displayUserServings(foodType)} <i>{this.t('daily servings per person')}</i></span>
                </div>
                <Slider
                  id={`food_average_slider_${foodType}`}
                  min={0.01}
                  max={3}
                  step={0.01}
                  ticks={4}
                  labels={['0', _upperFirst(this.props.t('average')), '2x', '3x']}
                  value={
                    userApiValue(
                      this.apiKey(foodType), this.props.user_footprint,
                      this.props.average_footprint,
                    ) / defaultApiValue(this.apiKey(foodType), this.props.average_footprint)
                  }
                  onChange={multiplier => this.updateMultiplier(foodType, multiplier)}
                />
              </div>
            </div>
          ))}
        </div>
        <NavButtons ui={this.props.ui} />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  widgetSource: s.widgetSource(state),
  user_footprint: s.user_footprint(state),
  average_footprint: s.average_footprint(state),
  ui: state.ui,
});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'computeUserFootprint',
  ]),
  dispatch,
);

export default wrap(
  withT('food'),
  connect(mapStateToProps, mapDispatchToProps),
  FoodComponent,
);
